import { observe } from "jotai-effect";
import {
    BookviewConversationAtom,
    ModalConversationAtom,
} from "./current-conversation/conversation-atoms";
import {
    ConversationStateStatus,
    CurrentConversationState,
} from "./current-conversation/conversation-atom.reducer";
import { loadConversation } from "./current-conversation/load-conversation.thunk";

let lastSyncedModalConversationId: string | undefined;
let lastSyncedBookviewConversationId: string | undefined;
let lastSyncedModalMessageCount = 0;
let lastSyncedBookviewMessageCount = 0;
let loadingModalConversationId: string | undefined;
let loadingBookviewConversationId: string | undefined;

interface ConversationState {
    conversationId: string | undefined;
    messageCount: number;
    status: ConversationStateStatus;
}

function extractConversationState(
    state: CurrentConversationState
): ConversationState {
    return {
        conversationId: state.currentConversation?.id,
        messageCount: state.currentConversation?.messages.length ?? 0,
        status: state.status,
    };
}

function updateTrackingAfterModalLoad(
    conversationId: string,
    modalMessageCount: number,
    bookviewMessageCount: number
): void {
    lastSyncedModalConversationId = conversationId;
    lastSyncedModalMessageCount = modalMessageCount;
    lastSyncedBookviewConversationId = conversationId;
    lastSyncedBookviewMessageCount = bookviewMessageCount;
    loadingModalConversationId = undefined;
}

function updateTrackingAfterBookviewLoad(
    conversationId: string,
    modalMessageCount: number,
    bookviewMessageCount: number
): void {
    lastSyncedModalConversationId = conversationId;
    lastSyncedModalMessageCount = modalMessageCount;
    lastSyncedBookviewConversationId = conversationId;
    lastSyncedBookviewMessageCount = bookviewMessageCount;
    loadingBookviewConversationId = undefined;
}

function shouldSyncModalFromBookview(
    bookview: ConversationState,
    modal: ConversationState,
    conversationId: string
): boolean {
    return (
        bookview.status === ConversationStateStatus.ConversationReady &&
        bookview.messageCount > lastSyncedBookviewMessageCount &&
        (bookview.messageCount > modal.messageCount ||
            lastSyncedModalConversationId !== conversationId) &&
        loadingModalConversationId !== conversationId &&
        modal.status !== ConversationStateStatus.ConversationLoading
    );
}

function shouldSyncBookviewFromModal(
    modal: ConversationState,
    bookview: ConversationState,
    conversationId: string
): boolean {
    return (
        modal.status === ConversationStateStatus.ConversationReady &&
        modal.messageCount > lastSyncedModalMessageCount &&
        (modal.messageCount > bookview.messageCount ||
            lastSyncedBookviewConversationId !== conversationId) &&
        loadingBookviewConversationId !== conversationId &&
        bookview.status !== ConversationStateStatus.ConversationLoading
    );
}

function handleLoadingCompletion(
    modal: ConversationState,
    bookview: ConversationState,
    conversationId: string
): boolean {
    if (
        loadingModalConversationId === conversationId &&
        modal.status === ConversationStateStatus.ConversationReady
    ) {
        updateTrackingAfterModalLoad(
            conversationId,
            modal.messageCount,
            bookview.messageCount
        );
        return true;
    }

    if (
        loadingBookviewConversationId === conversationId &&
        bookview.status === ConversationStateStatus.ConversationReady
    ) {
        updateTrackingAfterBookviewLoad(
            conversationId,
            modal.messageCount,
            bookview.messageCount
        );
        return true;
    }

    return false;
}

function handleSyncTriggering(
    modal: ConversationState,
    bookview: ConversationState,
    conversationId: string,
    set: Parameters<Parameters<typeof observe>[0]>[1]
): void {
    if (shouldSyncModalFromBookview(bookview, modal, conversationId)) {
        loadingModalConversationId = conversationId;
        set(ModalConversationAtom, async (dispatch) => {
            await loadConversation(conversationId)(dispatch);
        });
        return;
    }

    if (shouldSyncBookviewFromModal(modal, bookview, conversationId)) {
        loadingBookviewConversationId = conversationId;
        set(BookviewConversationAtom, async (dispatch) => {
            await loadConversation(conversationId)(dispatch);
        });
    }
}

function updateTrackingForDifferentConversations(
    modal: ConversationState,
    bookview: ConversationState
): void {
    if (modal.conversationId !== lastSyncedModalConversationId) {
        lastSyncedModalConversationId = modal.conversationId;
        lastSyncedModalMessageCount = modal.messageCount;
        loadingModalConversationId = undefined;
    }

    if (bookview.conversationId !== lastSyncedBookviewConversationId) {
        lastSyncedBookviewConversationId = bookview.conversationId;
        lastSyncedBookviewMessageCount = bookview.messageCount;
        loadingBookviewConversationId = undefined;
    }
}

export function conversationsAtomSyncEffect() {
    return observe((get, set) => {
        const modalState = get(ModalConversationAtom);
        const bookviewState = get(BookviewConversationAtom);

        const modal = extractConversationState(modalState);
        const bookview = extractConversationState(bookviewState);

        const isSameConversation =
            modal.conversationId &&
            bookview.conversationId &&
            modal.conversationId === bookview.conversationId;

        if (isSameConversation && modal.conversationId) {
            const conversationId = modal.conversationId;

            if (handleLoadingCompletion(modal, bookview, conversationId)) {
                return;
            }

            handleSyncTriggering(modal, bookview, conversationId, set);
        } else {
            updateTrackingForDifferentConversations(modal, bookview);
        }
    });
}
